/**
 * Idle-session tracker (v3.56.0).
 *
 * Why: we handle K-3 assessment data on devices that are shared between
 * adults in a school building, and a session used to persist until an
 * explicit logout. This module owns the "how long has nobody touched this
 * device" question for the WHOLE app — App.js mounts it once at the
 * authenticated layout level, never per page.
 *
 * Design notes that matter:
 *
 *  1. TIMESTAMP, NOT COUNTDOWN. Everything derives from `lastActivityAt`
 *     (epoch ms, persisted in localStorage so it is shared across tabs).
 *     Browsers throttle timers in background tabs, so a setInterval that
 *     decrements a counter drifts badly — a tab backgrounded for an hour
 *     would "have 20 minutes left" on wake. Every tick RECOMPUTES elapsed
 *     time from the stored timestamp instead, and `syncNow()` (wired to
 *     visibilitychange) forces that recompute the moment a tab wakes, so a
 *     tab that wakes past the threshold signs out immediately rather than
 *     waiting for a full cycle.
 *
 *  2. CROSS-TAB. Teachers routinely keep two tabs open. Activity in any tab
 *     writes the shared timestamp and broadcasts it, so every tab's timer
 *     resets together; a timeout or explicit logout in any tab broadcasts a
 *     sign-out so no tab is left authenticated. BroadcastChannel is the
 *     primary transport; where it is unsupported the localStorage `storage`
 *     event carries the same messages (writing the timestamp key already
 *     fires `storage` in sibling tabs, so the reset path degrades naturally).
 *
 *  3. NO REACT, NO FIREBASE. Pure module with injectable clock/storage so
 *     the unit tests can drive it with fake timers. The React glue lives in
 *     src/hooks/useIdleTimeout.js and the Firebase teardown in App.js.
 */

/** 30 minutes. THE idle threshold — the single source of truth; never
 *  re-derive or hardcode this value anywhere else. */
export const IDLE_TIMEOUT_MS = 30 * 60 * 1000;

/** Warn 2 minutes before expiry (i.e. at the 28-minute mark). */
export const WARNING_LEAD_MS = 2 * 60 * 1000;

/** Activity resets the timer at most this often — mousemove would otherwise
 *  thrash localStorage and the broadcast channel on every pixel. */
export const ACTIVITY_THROTTLE_MS = 5 * 1000;

/** How often the tracker re-evaluates elapsed idle time. Drives the modal's
 *  live countdown, so it is sub-second-accurate for display purposes. */
export const TICK_INTERVAL_MS = 1000;

/** DOM events that count as "someone is using this device". */
export const ACTIVITY_EVENTS = [
  'mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click',
];

export const STORAGE_KEY = 'literacy-app:lastActivityAt:v1';
export const SIGNOUT_KEY = 'literacy-app:sessionSignal:v1';
export const CHANNEL_NAME = 'literacy-app:idle:v1';

export const MSG_ACTIVITY = 'activity';
export const MSG_SIGNOUT = 'signout';

/** Safe localStorage wrapper — Safari private mode throws on write, and a
 *  storage failure must never break the session. */
function safeStorage(storage) {
  return {
    read() {
      try { return storage?.getItem(STORAGE_KEY) ?? null; } catch { return null; }
    },
    write(value) {
      try { storage?.setItem(STORAGE_KEY, String(value)); } catch { /* non-fatal */ }
    },
    signal(payload) {
      try { storage?.setItem(SIGNOUT_KEY, payload); } catch { /* non-fatal */ }
    },
    clear() {
      try {
        storage?.removeItem(STORAGE_KEY);
        storage?.removeItem(SIGNOUT_KEY);
      } catch { /* non-fatal */ }
    },
  };
}

/**
 * @param {object} deps
 * @param {() => number} [deps.now]            clock (injectable for tests)
 * @param {Storage}      [deps.storage]        localStorage (injectable)
 * @param {() => object|null} [deps.channelFactory] BroadcastChannel factory
 * @param {() => void}   deps.onWarn           fired once when entering the warning window
 * @param {() => void}   deps.onWarnCleared    fired when the warning window is left (activity elsewhere)
 * @param {(reason: string) => void} deps.onExpire  fired ONCE when idle >= IDLE_TIMEOUT_MS
 * @param {(remainingMs: number) => void} [deps.onTick] countdown feed for the modal
 * @param {number} [deps.idleTimeoutMs]        override (tests only)
 * @param {number} [deps.warningLeadMs]        override (tests only)
 */
export function createIdleTracker({
  now = () => Date.now(),
  storage = typeof window !== 'undefined' ? window.localStorage : null,
  channelFactory = defaultChannelFactory,
  onWarn = () => {},
  onWarnCleared = () => {},
  onExpire = () => {},
  onTick = () => {},
  idleTimeoutMs = IDLE_TIMEOUT_MS,
  warningLeadMs = WARNING_LEAD_MS,
  throttleMs = ACTIVITY_THROTTLE_MS,
  tickIntervalMs = TICK_INTERVAL_MS,
} = {}) {
  const store = safeStorage(storage);
  let channel = null;
  let intervalId = null;
  let lastBroadcastAt = 0;   // throttle bookkeeping (local, not shared)
  let warned = false;
  let expired = false;       // latch: onExpire fires exactly once
  let running = false;

  const readLastActivity = () => {
    const raw = store.read();
    const parsed = raw === null ? NaN : Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  };

  /** Write + broadcast a fresh activity timestamp. Ignores throttling. */
  const commitActivity = (at) => {
    store.write(at);
    lastBroadcastAt = at;
    if (warned) {
      warned = false;
      onWarnCleared();
    }
    try { channel?.postMessage({ type: MSG_ACTIVITY, at }); } catch { /* non-fatal */ }
  };

  /**
   * Record user activity. Throttled: at most one write/broadcast per
   * throttleMs, so a mousemove storm costs nothing after the first event.
   * The throttle is deliberately skipped while the warning modal is up —
   * that interaction must feel instant.
   */
  const recordActivity = () => {
    if (!running || expired) return;
    const at = now();
    if (!warned && at - lastBroadcastAt < throttleMs) return;
    commitActivity(at);
  };

  /** Explicit "Stay signed in" / programmatic extension — never throttled. */
  const extend = () => {
    if (!running || expired) return;
    commitActivity(now());
  };

  /**
   * Recompute state from the stored timestamp. Called on every tick and
   * (critically) on visibilitychange, so a throttled background tab
   * evaluates the TRUE elapsed time the instant it wakes.
   */
  const syncNow = () => {
    if (!running || expired) return;
    const last = readLastActivity();
    if (last === null) {
      // No timestamp yet (or it was cleared) — treat now as the baseline
      // rather than instantly expiring a fresh session.
      commitActivity(now());
      return;
    }
    const idleFor = now() - last;
    const remaining = idleTimeoutMs - idleFor;

    if (remaining <= 0) {
      expired = true;
      stopTicking();
      broadcastSignOut('timeout');
      onExpire('timeout');
      return;
    }

    onTick(remaining);

    if (remaining <= warningLeadMs && !warned) {
      warned = true;
      onWarn(remaining);
    } else if (remaining > warningLeadMs && warned) {
      warned = false;
      onWarnCleared();
    }
  };

  const broadcastSignOut = (reason) => {
    try { channel?.postMessage({ type: MSG_SIGNOUT, reason, at: now() }); } catch { /* non-fatal */ }
    // localStorage fallback: value must change to fire `storage` in siblings.
    store.signal(`${reason}:${now()}`);
  };

  /** A sibling tab told us the session ended. Latch and notify locally. */
  const handleRemoteSignOut = (reason) => {
    if (expired) return;
    expired = true;
    stopTicking();
    onExpire(reason || 'timeout');
  };

  const handleMessage = (data) => {
    if (!data || typeof data !== 'object') return;
    if (data.type === MSG_ACTIVITY && Number.isFinite(data.at)) {
      // Another tab saw activity: adopt its timestamp (never move backwards)
      // and drop out of the warning state.
      const last = readLastActivity();
      if (last === null || data.at > last) store.write(data.at);
      if (warned) {
        warned = false;
        onWarnCleared();
      }
    } else if (data.type === MSG_SIGNOUT) {
      handleRemoteSignOut(data.reason);
    }
  };

  /** localStorage `storage` event — the BroadcastChannel fallback. */
  const handleStorageEvent = (event) => {
    if (!event) return;
    if (event.key === SIGNOUT_KEY && event.newValue) {
      handleRemoteSignOut(String(event.newValue).split(':')[0]);
    } else if (event.key === STORAGE_KEY && event.newValue) {
      const at = Number(event.newValue);
      if (Number.isFinite(at) && warned) {
        warned = false;
        onWarnCleared();
      }
    }
  };

  const startTicking = () => {
    if (intervalId === null) intervalId = setInterval(syncNow, tickIntervalMs);
  };
  const stopTicking = () => {
    if (intervalId !== null) {
      clearInterval(intervalId);
      intervalId = null;
    }
  };

  const start = () => {
    if (running) return;
    running = true;
    expired = false;
    warned = false;
    channel = channelFactory(CHANNEL_NAME);
    if (channel) channel.onmessage = (event) => handleMessage(event?.data);
    // Seed the session baseline unless a sibling tab already set one — a
    // reload inside an idle window must NOT silently reset the clock.
    if (readLastActivity() === null) commitActivity(now());
    startTicking();
  };

  const stop = () => {
    running = false;
    stopTicking();
    try { channel?.close?.(); } catch { /* non-fatal */ }
    channel = null;
  };

  /** Explicit logout in THIS tab — tell every sibling, then clear state. */
  const signOutAllTabs = (reason = 'logout') => {
    broadcastSignOut(reason);
    expired = true;
    stop();
  };

  /** Drop the shared timestamp (called after teardown so the next session
   *  starts from a clean baseline). */
  const clearStoredState = () => store.clear();

  return {
    start,
    stop,
    recordActivity,
    extend,
    syncNow,
    signOutAllTabs,
    clearStoredState,
    handleStorageEvent,
    // test hooks
    _handleMessage: handleMessage,
    getState: () => ({
      running,
      warned,
      expired,
      lastActivityAt: readLastActivity(),
    }),
  };
}

function defaultChannelFactory(name) {
  if (typeof window === 'undefined' || typeof window.BroadcastChannel !== 'function') {
    return null; // caller falls back to the storage event
  }
  try { return new window.BroadcastChannel(name); } catch { return null; }
}

/** True when this browser needs the localStorage fallback transport. */
export function broadcastChannelSupported() {
  return typeof window !== 'undefined' && typeof window.BroadcastChannel === 'function';
}
